import type {
  SocialMedias,
  SocialUser,
} from '@features/social/api/social.service.types.ts';
import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@shared/utils/toast';
import { socialService } from '@features/social/api/social.service.ts';
// import { connectRapidMessagingSocket } from '@shared/api/rapid-messaging.socket.ts';
import { handleAxiosError } from '@shared/utils/error.ts';

export interface Social {
  _socialUser?: SocialUser;
  isLoadingUser: boolean;
  profileRevision: number;
}

export const useSocialStore = defineStore('social', {
  state: (): Social => ({
    _socialUser: undefined,
    isLoadingUser: true,
    profileRevision: 0,
  }),
  actions: {
    async getMyUser() {
      const revision = this.profileRevision;
      const authPlayer = useAuthStore().currentPlayer;
      if (this._socialUser?._id === authPlayer?.playerId) {
        this.isLoadingUser = false;
        return;
      }
      this._socialUser = undefined;
      try {
        const id = useAuthStore().currentPlayer?.playerId;
        if (!id) {
          toast.error({ message: 'Error getting user data' });
          return;
        }
        const res = await socialService.getMyUser();
        if (
          revision !== this.profileRevision ||
          authPlayer !== useAuthStore().currentPlayer
        )
          return;
        this._socialUser =
          res.data._id === useAuthStore().currentPlayer?.playerId
            ? res.data
            : undefined;
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response
          ?.status;
        if (status !== 404) handleAxiosError(error);
      } finally {
        // semi-global loader, it waits for all task in try (even if they fail or are not awaited)
        this.isLoadingUser = false;
      }
    },
    async refreshMyUser() {
      const revision = this.profileRevision;
      const authPlayer = useAuthStore().currentPlayer;
      try {
        const id = useAuthStore().currentPlayer?.playerId;
        if (!id) {
          toast.error({ message: 'Error getting user data' });
          return;
        }
        const res = await socialService.getMyUser();
        if (
          revision !== this.profileRevision ||
          authPlayer !== useAuthStore().currentPlayer
        )
          return;
        this._socialUser =
          res.data._id === useAuthStore().currentPlayer?.playerId
            ? res.data
            : undefined;
      } catch (err) {
        console.error(err);
        toast.error({ message: 'Error getting user data' });
      }
    },
    clearMyUser() {
      this.profileRevision += 1;
      this._socialUser = undefined;
      this.isLoadingUser = false;
    },
    checkMyUser() {
      if (!this._socialUser) {
        throw new Error(`You don't have a user`);
      }
    },
    async updateSocialUser(newData: Partial<SocialUser>) {
      try {
        this.checkMyUser();
        await socialService.updateSocialUser(newData);
      } catch (error) {
        toast.error({ message: 'User update failed. Please retry.' });
      }
    },
    async addPreferences(preferencesIds: string[]) {
      try {
        this.checkMyUser();
        await socialService.addPreferences(preferencesIds);
      } catch (error) {
        toast.error({ message: 'User update failed. Please retry.' });
      }
    },
    async removePreferences(preferencesIds: string[]) {
      try {
        this.checkMyUser();
        await socialService.removePreferences(preferencesIds);
      } catch (error) {
        toast.error({ message: 'User update failed. Please retry.' });
      }
    },
    async uploadSocialMedias(socialMedias: SocialMedias) {
      try {
        this.checkMyUser();
        await socialService.uploadSocialMedias(socialMedias);
      } catch (error) {
        toast.error({ message: 'User update failed. Please retry.' });
      }
    },
    async uploadUserImage(userImg: string) {
      try {
        this.checkMyUser();
        await socialService.uploadUserImage(userImg);
      } catch (error) {
        toast.error({ message: 'User update failed. Please retry.' });
      }
    },
    async setAvatar(avatarId: string) {
      try {
        this.checkMyUser();
        if (!this._socialUser?.RPMUserId)
          throw new Error(`You don't have an avatar`);
        const response = await socialService.saveAvatar(avatarId);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- because unity can't have started
        if (window.unityController) {
          window.unityController.changeAvatar(response.data.glbUrl);
        }
        this._socialUser.RPMAvatarId = response.data.RPMAvatarId;
        this._socialUser.glbUrl = response.data.glbUrl;
        toast.success({ message: 'Avatar updated successfully' });
      } catch (error) {
        toast.error({ message: 'Avatar creation failed. Try again.' });
      }
    },
    async createReadyPlayerMeUserId() {
      try {
        this.checkMyUser();
        if (!this._socialUser) return;
        const response = await socialService.postAvatarUserId();
        this._socialUser.RPMUserId = response.data;
      } catch (error) {
        console.error(error);
      }
    },
  },
});

export default useSocialStore;
